import type { RoadsData, CarriagewayFootway, CutFillSection } from '../types/tender.types';
import { DEFAULT_FOOTPATHS_CONFIG, calculateFootpathsAverageBuildUp } from '../components/forms/FootpathsConfigModal';
import { groupCutFillSectionsByRoad, computeLiveCutFillGroups, computeLiveFootpathsGroups, dedupeSectionsByChainage } from './roadCalculations';
import { formatBaseAttachments } from './constants';
import { findCarriagewaySummaryEvaluateCode } from './roadsEvaluateCodesMapping';

const FOOTPATH_AREA_KEYS: (keyof CarriagewayFootway)[] = [
  'tarmacPath', 'tarmacCrossover', 'heavyDutyCrossover',
  'blockPavedPathCrossover', 'cycleway', 'verge',
];

export interface RoadSiteStripBreakdown {
  /** That road's Carriageway Take-off "Total Road Area" (`carriagewayFootway.totalRoadArea`) */
  roadAreaByRoadId: Record<string, number>;
  /** Sum of the road's own footpath area fields (tarmacPath, cycleway, verge, etc.) */
  pathTotalByRoadId: Record<string, number>;
  /** roadArea + pathTotal */
  siteStripAreaByRoadId: Record<string, number>;
}

/**
 * Single source of truth for `/roads-sewers/roads` → Carriageway Summary's per-road
 * "site strip area" (`RoadsBillSummary.tsx`'s "Site strip to remove vegetation" and
 * "Excavate to reduce levels to take up Xmm top soil" rows both derive from this).
 * Carriageway Summary calls this directly rather than re-deriving it inline, so any
 * future change to the formula only needs to happen here.
 *
 * Road area comes straight from `carriagewayFootway.totalRoadArea` rather than summing
 * `cutFillSections.table[].roadArea` — that per-row field is only kept in sync live in
 * RoadsTakeoff's `cutFillCalculatedRoadGroups` (see its comment), never written back to
 * the stored table, so summing the stored rows went stale per-road (surfaced as Road 4's
 * "Site strip to remove vegetation" being wrong while other roads happened to still match).
 */
export const calcRoadSiteStripBreakdown = (roadsData: RoadsData | undefined): RoadSiteStripBreakdown => {
  const roads = roadsData?.carriagewayFootways.table || [];

  const roadAreaByRoadId: Record<string, number> = {};
  const pathTotalByRoadId: Record<string, number> = {};
  const siteStripAreaByRoadId: Record<string, number> = {};

  roads.forEach((road) => {
    const roadArea = road.totalRoadArea || 0;
    const pathTotal = FOOTPATH_AREA_KEYS.reduce((sum, key) => sum + ((road[key] as number) || 0), 0);
    roadAreaByRoadId[road.id] = roadArea;
    pathTotalByRoadId[road.id] = pathTotal;
    siteStripAreaByRoadId[road.id] = roadArea + pathTotal;
  });

  return { roadAreaByRoadId, pathTotalByRoadId, siteStripAreaByRoadId };
};

export interface RoadTopsoilExcavation {
  byRoadId: Record<string, number>;
  total: number;
  stripDepth: number;
  stripMm: number;
}

/**
 * Same value as Carriageway Summary's "Excavate to reduce levels to take up Xmm top
 * soil, deposit onsite" row (siteStripArea × strip depth), summed per road. Consumed
 * by both `RoadsBillSummary.tsx` (Carriageway Summary itself) and
 * `SpoilReconciliation.tsx` ("Road / Footpaths" Strip) so the two can never drift —
 * if this formula ever changes, both callers pick it up automatically.
 */
export const calcRoadTopsoilExcavation = (roadsData: RoadsData | undefined): RoadTopsoilExcavation => {
  const stripDepth = roadsData?.cutFillSections.config.stripValueDefault || 0.3;
  const { siteStripAreaByRoadId } = calcRoadSiteStripBreakdown(roadsData);

  const byRoadId: Record<string, number> = {};
  let total = 0;
  Object.entries(siteStripAreaByRoadId).forEach(([roadId, area]) => {
    const value = area * stripDepth;
    byRoadId[roadId] = value;
    total += value;
  });

  return { byRoadId, total, stripDepth, stripMm: Math.round(stripDepth * 1000) };
};

/** Total verge area (m²) across every road — `road.verge` summed, take-off data. */
export const calcVergeTotal = (roadsData: RoadsData | undefined): number =>
  (roadsData?.carriagewayFootways.table || []).reduce((sum, road) => sum + (road.verge || 0), 0);

/**
 * `roadsData.footpaths.config` (Footpaths — Construction Configuration), falling back to
 * `DEFAULT_FOOTPATHS_CONFIG` before it's ever been configured — same fallback rule
 * `buildRoadsBillSummarySections` below applies inline.
 */
export const getFootpathsConfig = (roadsData: RoadsData | undefined): typeof DEFAULT_FOOTPATHS_CONFIG =>
  (roadsData?.footpaths.config as typeof DEFAULT_FOOTPATHS_CONFIG)?.tarmacPath !== undefined
    ? (roadsData!.footpaths.config as typeof DEFAULT_FOOTPATHS_CONFIG)
    : DEFAULT_FOOTPATHS_CONFIG;

// ── Carriageway (Roads) Bill Summary section tree ───────────────────────────
// Single source of truth for `/roads-sewers/roads`'s Carriageway Summary bill —
// shared by `RoadsBillSummary.tsx` (renders it) and `useRoadsAndSewersTotalSync.ts`
// (sums it into the Sum Analysis TENDER TOTAL), so the two can never drift.

export interface RoadsSummaryFormulaRow { ref: string; value: number; unit: string }

export interface RoadsSummaryFormulaInfo {
  label: string;
  formula: string;
  excelRef: string;
  rows: RoadsSummaryFormulaRow[];
  total: number;
  unit: string;
}

export interface RoadsSummaryValueEntry {
  qty: number | null;
  formulaInfo?: RoadsSummaryFormulaInfo;
  naOverride?: boolean;
  /** When set, this road's cell renders as an editable NumberInput bound to roadsData.manualQtys[manualKey] */
  manualKey?: string;
}

export interface RoadsSummaryRowNode {
  key: string;
  description: string;
  unit: string;
  rowType: 'sub-header' | 'item';
  values: Record<string, RoadsSummaryValueEntry>;
  /** Item library resource code for this row, when known — used only to render the
   * dev-mode-only "Evaluate Code" column + its looked-up rate. Most rows have none yet. */
  evaluateCode?: string;
  /** Exempts this row from pruneZeroQuantityRoadsSections below — it must always render
   * (as 0, not "N/A"/pruned away) even when not applicable to any road. */
  alwaysShow?: boolean;
}

export interface RoadsSummarySectionNode {
  key: string;
  description: string;
  rowType: 'section-header';
  children: RoadsSummaryRowNode[];
}

export type RoadsSummaryRow = RoadsSummarySectionNode | RoadsSummaryRowNode;

/**
 * Drop every 'item' row whose quantity is 0 across every road (not per-cell —
 * a row can legitimately be 0 on one road and non-zero on another), then drop
 * any now-orphaned 'sub-header' row that has no 'item' row left before the
 * next 'sub-header'/end of section, then drop any section left with no
 * children at all. Only called with `pruneZeroQty: true` (the default) —
 * `RoadsBillSummary.tsx`'s `hideZeroQtyRows` prop, `false` on the take-off
 * page's own Carriageway Summary tab (`CarriagewaySummary.tsx`), skips this
 * function entirely there, which is what actually keeps every manual-entry row
 * (`manualKey`) visible/editable for data entry — so unlike an earlier version
 * of this function, a manual row on /roads-sewers/review is pruned exactly like
 * any other row when every road's value is still 0/unset; it isn't a blanket
 * exemption. Rows flagged `alwaysShow` (e.g. Verges' topsoil row) remain
 * exempt — they must always appear, showing 0 rather than being pruned,
 * regardless of quantity, on both pages.
 */
function pruneZeroQuantityRoadsSections(sections: RoadsSummarySectionNode[]): RoadsSummarySectionNode[] {
  const isEmptyItemRow = (row: RoadsSummaryRowNode): boolean => {
    if (row.rowType !== 'item') return false;
    if (row.alwaysShow) return false;
    const values = Object.values(row.values);
    const total = values.reduce((sum, v) => sum + (v.qty ?? 0), 0);
    // Round the same way the Total column itself displays (fmtQty/roundNearest
    // = Math.round) — a raw total like 0.4 reads as "0" on screen but isn't
    // === 0, so comparing the unrounded sum left rows that visibly show 0
    // still in the table.
    return Math.round(total) === 0;
  };
  return sections.reduce<RoadsSummarySectionNode[]>((acc, section) => {
    const afterItemPrune = section.children.filter((row) => !isEmptyItemRow(row));
    const finalChildren = afterItemPrune.filter((row, idx) => {
      if (row.rowType !== 'sub-header') return true;
      for (let i = idx + 1; i < afterItemPrune.length; i++) {
        if (afterItemPrune[i].rowType === 'sub-header') return false;
        if (afterItemPrune[i].rowType === 'item') return true;
      }
      return false;
    });
    if (finalChildren.length > 0) acc.push({ ...section, children: finalChildren });
    return acc;
  }, []);
}

type CfwSpecKey =
  | 'tarmacSpec1'
  | 'tarmacSpec2'
  | 'tarmacSpec3'
  | 'tarmacSpec4'
  | 'blockPavedSpec1'
  | 'blockPavedSpec2'
  | 'blockPavedSpec3'
  | 'blockPavedSpec4';

type CfwFootpathKey =
  | 'tarmacPath'
  | 'tarmacCrossover'
  | 'heavyDutyCrossover'
  | 'blockPavedPathCrossover'
  | 'cycleway'
  | 'verge';

/**
 * Builds the full Carriageway Summary section tree straight off `roadsData` — every
 * input (roads, cut/fill sections, road types, footpaths config, manual quantities)
 * is read directly off it, so this is a pure function of `roadsData` (and `options`) alone.
 */
export const buildRoadsBillSummarySections = (
  roadsData: RoadsData | undefined,
  options?: { pruneZeroQty?: boolean },
): RoadsSummarySectionNode[] => {
  const roads = roadsData?.carriagewayFootways.table || [];
  if (!roadsData || roads.length === 0) return [];

  const cutFillSections = roadsData.cutFillSections.table || [];
  const roadTypes = roadsData.roadTypes.table || [];
  // `footpaths.config` is typed as `FootpathsConstructionConfig | Record<string, never>`
  // (empty before it's ever been configured) — fall back to the default build-up then.
  const footpathsConfig: typeof DEFAULT_FOOTPATHS_CONFIG =
    (roadsData.footpaths.config as typeof DEFAULT_FOOTPATHS_CONFIG)?.tarmacPath !== undefined
      ? (roadsData.footpaths.config as typeof DEFAULT_FOOTPATHS_CONFIG)
      : DEFAULT_FOOTPATHS_CONFIG;
  const manualQtys = roadsData.manualQtys ?? {};

  const stripValueDefault = roadsData.cutFillSections.config.stripValueDefault ?? 0.3;
  const kerbRaceWidth = roadsData.carriagewayFootways.config.kerbRaceWidth ?? 0.5;
  const footpathsAverageBuildUp = calculateFootpathsAverageBuildUp(footpathsConfig);

  // roadArea/cappingArea/cutVolume/fillVolume/footpathsCutVolume/footpathsFillVolume/pathArea
  // are derived fields (see computeCutFillFields/computeFootpathsFields, roadCalculations.ts) —
  // not stored on roadsData.cutFillSections.table, only computed live here (same source
  // RoadsTakeoff.tsx's Cut & Fill/Footpaths/Capping Layers tabs use).
  const roadGroups = groupCutFillSectionsByRoad(cutFillSections, roads);
  const cutFillGroups = computeLiveCutFillGroups(roadGroups, roads, roadTypes, stripValueDefault, kerbRaceWidth);
  const footpathsGroups = computeLiveFootpathsGroups(roadGroups, roads, stripValueDefault, footpathsAverageBuildUp);

  const earthworksByRoad: Record<string, {
    sections: CutFillSection[];
    roadArea: number;
    cappingArea: number;
    stripTopsoil: number;
    cutVolume: number;
    footpathsCutVolume: number;
    fillVolume: number;
    footpathsFillVolume: number;
    pathArea: number;
  }> = {};
  roads.forEach((road) => {
    const cutFillSectionsLive = cutFillGroups.find((g) => g.roadId === road.id)?.sections ?? [];
    const footpathsSectionsLive = footpathsGroups.find((g) => g.roadId === road.id)?.sections ?? [];
    // Dedupe by chainage before summing — roadArea/cappingArea/cutVolume/fillVolume
    // are already divided by the distinct chainage count (see computeLiveCutFillGroups),
    // so summing the raw (possibly duplicate-chainage) rows double-counts. See same
    // fix in RoadsTakeoff.tsx.
    const distinctCutFill = dedupeSectionsByChainage(cutFillSectionsLive);
    const distinctFootpaths = dedupeSectionsByChainage(footpathsSectionsLive);
    earthworksByRoad[road.id] = {
      sections: cutFillSectionsLive,
      roadArea: road.totalRoadArea || 0,
      cappingArea: distinctCutFill.reduce((acc, c) => acc + (c.cappingArea || 0), 0),
      stripTopsoil: distinctCutFill.reduce((acc, c) => acc + (c.roadArea || 0) * (c.stripValue || 0), 0),
      cutVolume: distinctCutFill.reduce((acc, c) => acc + (c.cutVolume || 0), 0),
      footpathsCutVolume: distinctFootpaths.reduce((acc, c) => acc + (c.footpathsCutVolume || 0), 0),
      fillVolume: distinctCutFill.reduce((acc, c) => acc + (c.fillVolume || 0), 0),
      footpathsFillVolume: distinctFootpaths.reduce((acc, c) => acc + (c.footpathsFillVolume || 0), 0),
      pathArea: distinctFootpaths.reduce((acc, c) => acc + (c.pathArea || 0), 0),
    };
  });

  const tarmacRoadTypes = roadTypes.filter((rt) => rt.surface !== 'Block Paved Road');
  const blockPavedRoadTypes = roadTypes.filter((rt) => rt.surface === 'Block Paved Road');

  const specConfigs: { specKey: CfwSpecKey; label: string; roadType: (typeof roadTypes)[number] | null; excelCol: string }[] = [
    { specKey: 'tarmacSpec1', label: 'Tarmac Spec 1', roadType: tarmacRoadTypes[0] ?? null, excelCol: 'C$24' },
    { specKey: 'tarmacSpec2', label: 'Tarmac Spec 2', roadType: tarmacRoadTypes[1] ?? null, excelCol: 'D$24' },
    { specKey: 'tarmacSpec3', label: 'Tarmac Spec 3', roadType: tarmacRoadTypes[2] ?? null, excelCol: 'E$24' },
    { specKey: 'tarmacSpec4', label: 'Tarmac Spec 4', roadType: tarmacRoadTypes[3] ?? null, excelCol: 'F$24' },
    { specKey: 'blockPavedSpec1', label: 'Block Paved Spec 1', roadType: blockPavedRoadTypes[0] ?? null, excelCol: 'G$24' },
    { specKey: 'blockPavedSpec2', label: 'Block Paved Spec 2', roadType: blockPavedRoadTypes[1] ?? null, excelCol: 'H$24' },
    { specKey: 'blockPavedSpec3', label: 'Block Paved Spec 3', roadType: blockPavedRoadTypes[2] ?? null, excelCol: 'I$24' },
    { specKey: 'blockPavedSpec4', label: 'Block Paved Spec 4', roadType: blockPavedRoadTypes[3] ?? null, excelCol: 'J$24' },
  ];

  const footpathConfigs: { areaKey: CfwFootpathKey; label: string; config: (typeof footpathsConfig)['tarmacPath']; excelCol: string }[] = [
    { areaKey: 'tarmacPath', label: 'Tarmac Footpath', config: footpathsConfig.tarmacPath, excelCol: 'AI$24' },
    { areaKey: 'tarmacCrossover', label: 'Tarmac Crossover', config: footpathsConfig.tarmacCrossover, excelCol: 'AJ$24' },
    { areaKey: 'heavyDutyCrossover', label: 'Heavy Duty Crossover', config: footpathsConfig.heavyDutyCrossover, excelCol: 'AK$24' },
    { areaKey: 'blockPavedPathCrossover', label: 'Block Paved Path / Crossover', config: footpathsConfig.blockPavedCrossover, excelCol: 'AL$24' },
    { areaKey: 'cycleway', label: 'Cycleway', config: footpathsConfig.cycleway, excelCol: 'AM$24' },
    { areaKey: 'verge', label: 'Verges', config: footpathsConfig.verge, excelCol: 'AN$24' },
  ];

  // Shared with SpoilReconciliation.tsx's "Road / Footpaths" Strip value — see
  // calcRoadSiteStripBreakdown/calcRoadTopsoilExcavation above. Any formula change belongs
  // there, not here.
  const siteStripBreakdown = calcRoadSiteStripBreakdown(roadsData);
  const topsoilExcavation = calcRoadTopsoilExcavation(roadsData);
  const { stripDepth, stripMm } = topsoilExcavation;
  const usedRoadTypeIds = new Set(cutFillSections.map((s) => s.roadTypeId));
  const selectedTarmacConstructionTypeId = roadsData.cutFillSections.config.tarmacConstructionTypeId;
  const selectedBlockPavedConstructionTypeId = roadsData.cutFillSections.config.blockPavedConstructionTypeId;

  const sectionList: RoadsSummarySectionNode[] = [];
  const sectionMap = new Map<string, RoadsSummarySectionNode>();
  const rowMap = new Map<string, RoadsSummaryRowNode>();
  let currentSection: RoadsSummarySectionNode | null = null;

  const addSection = (id: string, desc: string) => {
    let s = sectionMap.get(id);
    if (!s) {
      s = { key: id, description: desc, rowType: 'section-header', children: [] };
      sectionMap.set(id, s);
      sectionList.push(s);
    }
    currentSection = s;
  };
  const ensureRow = (id: string, desc: string, unit: string, rowType: 'sub-header' | 'item', evaluateCode?: string): RoadsSummaryRowNode => {
    let r = rowMap.get(id);
    if (!r) {
      r = { key: id, description: desc, unit, rowType, values: {}, evaluateCode };
      rowMap.set(id, r);
      currentSection!.children.push(r);
    }
    return r;
  };
  const addSubHeader = (id: string, desc: string) => ensureRow(id, desc, '', 'sub-header');
  const addItem = (id: string, desc: string, unit: string): RoadsSummaryRowNode =>
    ensureRow(id, desc, unit, 'item', findCarriagewaySummaryEvaluateCode(desc));
  const addManualItem = (id: string, desc: string, unit: string, manualKeyPrefix: string, roadId: string, naOverride = false) => {
    const r = addItem(id, desc, unit);
    const manualKey = `${manualKeyPrefix}-${roadId}`;
    r.values[roadId] = naOverride ? { qty: null, naOverride: true } : { qty: manualQtys[manualKey] ?? null, manualKey };
  };

  roads.forEach((road, idx) => {
    const ew = earthworksByRoad[road.id] ?? {
      sections: [], roadArea: 0, cappingArea: 0, stripTopsoil: 0, cutVolume: 0, footpathsCutVolume: 0, fillVolume: 0,
      footpathsFillVolume: 0, pathArea: 0,
    };

    // ── SITE CLEARANCE & EARTHWORKS ─────────────────────────────────────────
    addSection('sec-site-clearance', 'SITE CLEARANCE & EARTHWORKS');

    const pathTotal = siteStripBreakdown.pathTotalByRoadId[road.id] || 0;
    const siteStripArea = siteStripBreakdown.siteStripAreaByRoadId[road.id] || 0;
    const siteStripRoadArea = siteStripBreakdown.roadAreaByRoadId[road.id] || 0;

    addItem('site-strip', 'Site strip to remove vegetation', 'm²').values[road.id] = {
      qty: siteStripArea || null,
      formulaInfo: {
        label: 'Site strip to remove vegetation',
        formula: 'roadArea + Σ(pathAreas)',
        excelRef: 'K24 + AO24',
        rows: [
          { ref: 'Road area', value: siteStripRoadArea, unit: 'm²' },
          { ref: 'Path area', value: pathTotal, unit: 'm²' },
        ],
        total: siteStripArea,
        unit: 'm²',
      },
    };

    addItem('topsoil-excavation', `Excavate to reduce levels to take up ${stripMm}mm top soil, deposit onsite`, 'm³').values[road.id] = {
      qty: topsoilExcavation.byRoadId[road.id] || null,
      formulaInfo: {
        label: 'Topsoil excavation',
        formula: `siteStripArea × ${stripMm}mm strip depth`,
        excelRef: 'J7 × BB$21',
        rows: [
          { ref: 'Site strip area', value: siteStripArea, unit: 'm²' },
          { ref: 'Strip depth', value: stripDepth, unit: 'm' },
        ],
        total: topsoilExcavation.byRoadId[road.id] || 0,
        unit: 'm³',
      },
    };

    // ── EXCAVATING & FILLING ─────────────────────────────────────────────────
    addSection('sec-excavating-filling', 'EXCAVATING & FILLING');

    addItem('excavate-reduce-levels', 'Excavate to reduce levels', 'm³').values[road.id] = {
      qty: (ew.cutVolume + ew.footpathsCutVolume) || null,
      formulaInfo: {
        label: 'Excavate to reduce levels',
        formula: 'Σ(Carriageway Cut & Fill → Cut Vol) + Σ(Footpaths → Cut Vol)',
        excelRef: 'BF126 + BM126',
        rows: [
          ...ew.sections.filter((s) => (s.cutVolume || 0) > 0).map((s) => ({ ref: s.chainage || s.id, value: s.cutVolume, unit: 'm³' })),
          ...ew.sections.filter((s) => (s.footpathsCutVolume || 0) > 0).map((s) => ({ ref: s.chainage || s.id, value: s.footpathsCutVolume, unit: 'm³' })),
        ],
        total: ew.cutVolume + ew.footpathsCutVolume,
        unit: 'm³',
      },
    };

    addItem('fill-subsoil-paths', 'Filling with site won subsoil to paths', 'm³').values[road.id] = {
      qty: ew.footpathsFillVolume || null,
      formulaInfo: {
        label: 'Filling with site won subsoil to paths',
        formula: 'Σ(cutFillSection.footpathsFillVolume) where footpathsDepth < 0',
        excelRef: '−BN126',
        rows: ew.sections
          .filter((s) => (s.footpathsFillVolume || 0) > 0)
          .map((s) => ({ ref: s.chainage || s.id, value: s.footpathsFillVolume, unit: 'm³' })),
        total: ew.footpathsFillVolume,
        unit: 'm³',
      },
    };

    addManualItem('fill-6f2-footpaths', 'Filling with recycled 6F2 to footpaths', 'm³', 'footpaths-fill-6f2', road.id);

    addItem('fill-6f2-roads', 'Filling with recycled 6F2 to roads', 'm³').values[road.id] = {
      qty: ew.fillVolume || null,
      formulaInfo: {
        label: 'Filling with recycled 6F2 to roads',
        formula: 'Σ(cutFillSection.fillVolume) where depth < 0',
        excelRef: '−BG126',
        rows: ew.sections
          .filter((s) => (s.fillVolume || 0) > 0)
          .map((s) => ({ ref: s.chainage || s.id, value: s.fillVolume, unit: 'm³' })),
        total: ew.fillVolume,
        unit: 'm³',
      },
    };

    const levelAndCompactArea = pathTotal + ew.cappingArea;
    addItem('level-compact-formation', 'Level and compact formation', 'm²').values[road.id] = {
      qty: levelAndCompactArea || null,
      formulaInfo: {
        label: 'Level and compact formation',
        formula: 'Σ(pathAreas) + Σ(cappingArea)',
        excelRef: 'AO24 + Σ(BF24)',
        rows: [
          { ref: 'Path area', value: pathTotal, unit: 'm²' },
          { ref: 'Capping area', value: ew.cappingArea, unit: 'm²' },
        ],
        total: levelAndCompactArea,
        unit: 'm²',
      },
    };

    // ── CARRIAGEWAYS ─────────────────────────────────────────────────────────
    addSection('sec-carriageways', 'CARRIAGEWAYS');
    // Same Tarmac/Block Capping formula as Carriageway Cut & Fill's accordion header
    // (RoadsTakeoff.tsx): cappingRatio = totalCapping / totalRoadArea, applied to the
    // road's tarmac/block-paved spec area totals.
    const cappingRatio = ew.roadArea > 0 ? ew.cappingArea / ew.roadArea : 0;
    const sumTarmac = (road.tarmacSpec1 || 0) + (road.tarmacSpec2 || 0) + (road.tarmacSpec3 || 0) + (road.tarmacSpec4 || 0);
    const sumBlock = (road.blockPavedSpec1 || 0) + (road.blockPavedSpec2 || 0) + (road.blockPavedSpec3 || 0) + (road.blockPavedSpec4 || 0);
    const tarmacCapping = sumTarmac * cappingRatio;
    const blockCapping = sumBlock * cappingRatio;
    specConfigs.forEach(({ specKey, label, roadType, excelCol }) => {
      // Spec sub-sections (Tarmac Spec N / Block Paved Spec N) always render, even when
      // that spec has no road type defined or isn't used by any Cut & Fill section —
      // `isApplicable` gates every value in the group to N/A instead of hiding the group.
      const isApplicable = !!roadType && usedRoadTypeIds.has(roadType.id);
      const isBlockPaved = specKey.startsWith('blockPavedSpec');
      const rt = roadType ?? {
        id: `unconfigured-${specKey}`,
        name: isBlockPaved ? 'Block Paved Access Road' : 'Macadam Access Road',
        surface: isBlockPaved ? 'Block Paved Road' : 'Tarmac Road',
        membrane: false,
        surfaceCourse: { type: isBlockPaved ? '200x100x60mm Keybloc Paving' : 'HRA 55/10', thickness: 0 },
        binderCourse: { thickness: 0 },
        baseCourse: { thickness: 0 },
        subBase: { type: 'Type 1', thickness: 0 },
        capping: 0,
        eoPreCoat: false,
        eoPSV65: false,
        sandPave: 0,
      };
      const specArea = isApplicable ? ((road[specKey] as number) || 0) : 0;
      const roadTypeTitle = rt.name;
      addSubHeader(`subheader-spec-${specKey}`, `${label}  —  ${roadTypeTitle}`);
      const baseCourseLabel = roadsData?.capping.config.baseCourseType ?? 'AC32';
      const baseAttachmentSuffix = formatBaseAttachments(roadsData?.capping.config.baseAttachments);
      const binderAttachmentSuffix = formatBaseAttachments(roadsData?.capping.config.binderAttachments);
      const binderCourseLabel = roadsData?.capping.config.binderCourseType ?? 'AC20';
      const subBaseLabel = rt.subBase.type === '6F2' ? '6F2' : 'MoT type 1';
      // When a Tarmac/Block Paved Construction Type is selected in the Cut & Fill Configure modal,
      // only that group's selected spec carries a real capping layer / sub base quantity — the
      // other specs in the same group show N/A, since capping/sub base are shared site-wide
      // construction and would otherwise double-count.
      const selectedConstructionTypeId = rt.surface === 'Block Paved Road' ? selectedBlockPavedConstructionTypeId : selectedTarmacConstructionTypeId;
      const isSelectedConstructionType = isApplicable && (!selectedConstructionTypeId || rt.id === selectedConstructionTypeId);

      const specFormula: RoadsSummaryFormulaInfo = {
        label: `${label} area`,
        formula: `road.${specKey} from carriageway take-off`,
        excelRef: `SUM(${excelCol})`,
        rows: [{ ref: label, value: specArea, unit: 'm²' }],
        total: specArea,
        unit: 'm²',
      };

      if (rt.surface !== 'Block Paved Road') {
        const membraneOn = isApplicable && rt.membrane;
        addItem(`spec-${specKey}-membrane`, 'Membrane / Geotextile', 'm²').values[road.id] = {
          qty: membraneOn ? (ew.cappingArea || null) : null,
          formulaInfo: {
            label: 'Membrane / Geotextile',
            formula: 'Σ(cappingArea) — only when roadType.membrane is Yes',
            excelRef: 'Σ(BF24)',
            rows: [{ ref: 'Capping area', value: ew.cappingArea, unit: 'm²' }],
            total: ew.cappingArea,
            unit: 'm²',
          },
          naOverride: !membraneOn,
        };
        const tarmacCappingFormula: RoadsSummaryFormulaInfo = {
          label: `${label} capping area`,
          formula: 'Σ(tarmac spec areas) × (Σ(cappingArea) ÷ Σ(roadArea)) — Carriageway Cut & Fill\'s Tarmac Capping',
          excelRef: 'Tarmac Capping',
          rows: [{ ref: 'Tarmac Capping', value: tarmacCapping, unit: 'm²' }],
          total: tarmacCapping,
          unit: 'm²',
        };
        const cappingOn = isApplicable && rt.capping > 0 && isSelectedConstructionType;
        addItem(`spec-${specKey}-capping`, rt.capping > 0 ? `${Math.round(rt.capping * 1000)}mm thick ${roadsData?.capping.config.cappingType ?? 'R/C 6F2 capping'} layer` : `xxmm thick ${roadsData?.capping.config.cappingType ?? 'R/C 6F2 capping'} layer`, 'm²').values[road.id] = {
          qty: cappingOn ? (tarmacCapping || null) : null,
          formulaInfo: tarmacCappingFormula,
          naOverride: !cappingOn,
        };
        const subBaseOn = isApplicable && rt.subBase.thickness > 0 && isSelectedConstructionType;
        addItem(`spec-${specKey}-subbase`, rt.subBase.thickness > 0 ? `${Math.round(rt.subBase.thickness * 1000)}mm thick ${subBaseLabel} Sub Base` : `xxmm thick ${subBaseLabel} Sub Base`, 'm²').values[road.id] = {
          qty: subBaseOn ? (tarmacCapping || null) : null,
          formulaInfo: tarmacCappingFormula,
          naOverride: !subBaseOn,
        };
        const baseCourseOn = isApplicable && rt.baseCourse.thickness > 0;
        addItem(`spec-${specKey}-basecourse`, rt.baseCourse.thickness > 0 ? `${Math.round(rt.baseCourse.thickness * 1000)}mm thick ${baseCourseLabel} DBM Base Course${baseAttachmentSuffix}` : `xxmm thick ${baseCourseLabel} DBM Base Course${baseAttachmentSuffix}`, 'm²').values[road.id] = {
          qty: baseCourseOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !baseCourseOn,
        };
        addManualItem(`spec-${specKey}-hotbond1`, 'Hot bond coat between layers', 'm²', `hot-bond-1-${specKey}`, road.id, !isApplicable);
        const binderCourseOn = isApplicable && rt.binderCourse.thickness > 0;
        addItem(`spec-${specKey}-bindercourse`, rt.binderCourse.thickness > 0 ? `${Math.round(rt.binderCourse.thickness * 1000)}mm thick ${binderCourseLabel} DBM Binder Course${binderAttachmentSuffix}` : `xxmm thick ${binderCourseLabel} DBM Binder Course${binderAttachmentSuffix}`, 'm²').values[road.id] = {
          qty: binderCourseOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !binderCourseOn,
        };
        addItem(`spec-${specKey}-sealinggrit`, 'Sealing grit', 'm²').values[road.id] = { qty: isApplicable ? (specArea || null) : null, formulaInfo: specFormula, naOverride: !isApplicable };
        addItem(`spec-${specKey}-sweeptack`, 'Sweep tack', 'm²').values[road.id] = { qty: isApplicable ? (specArea || null) : null, formulaInfo: specFormula, naOverride: !isApplicable };
        addManualItem(`spec-${specKey}-hotbond2`, 'Hot bond coat between layers', 'm²', `hot-bond-2-${specKey}`, road.id, !isApplicable);
        const surfaceCourseOn = isApplicable && rt.surfaceCourse.thickness > 0;
        addItem(`spec-${specKey}-surfacecourse`, rt.surfaceCourse.thickness > 0 ? `${Math.round(rt.surfaceCourse.thickness * 1000)}mm thick ${rt.surfaceCourse.type} Surface Course` : `xxmm thick ${rt.surfaceCourse.type} Surface Course`, 'm²').values[road.id] = {
          qty: surfaceCourseOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !surfaceCourseOn,
        };
        const eoPreCoatOn = isApplicable && rt.eoPreCoat;
        addItem(`spec-${specKey}-eoprecoat`, 'EO Pre Coated Chippings', 'm²').values[road.id] = {
          qty: eoPreCoatOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !eoPreCoatOn,
        };
        const eoPSV65On = isApplicable && rt.eoPSV65;
        addItem(`spec-${specKey}-eopsv65`, 'EO PSV 65+', 'm²').values[road.id] = {
          qty: eoPSV65On ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !eoPSV65On,
        };
      } else {
        const membraneOn = isApplicable && rt.membrane;
        addItem(`spec-${specKey}-membrane`, 'Membrane', 'm²').values[road.id] = {
          qty: membraneOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !membraneOn,
        };
        const blockCappingFormula: RoadsSummaryFormulaInfo = {
          label: `${label} capping area`,
          formula: 'Σ(block paved spec areas) × (Σ(cappingArea) ÷ Σ(roadArea)) — Carriageway Cut & Fill\'s Block Capping',
          excelRef: 'Block Capping',
          rows: [{ ref: 'Block Capping', value: blockCapping, unit: 'm²' }],
          total: blockCapping,
          unit: 'm²',
        };
        const cappingOn = isApplicable && rt.capping > 0 && isSelectedConstructionType;
        addItem(`spec-${specKey}-capping`, rt.capping > 0 ? `${Math.round(rt.capping * 1000)}mm thick ${roadsData?.capping.config.cappingType ?? 'R/C 6F2 capping'} layer` : `xxmm thick ${roadsData?.capping.config.cappingType ?? 'R/C 6F2 capping'} layer`, 'm²').values[road.id] = {
          qty: cappingOn ? (blockCapping || null) : null,
          formulaInfo: blockCappingFormula,
          naOverride: !cappingOn,
        };
        const subBaseOn = isApplicable && rt.subBase.thickness > 0 && isSelectedConstructionType;
        addItem(`spec-${specKey}-subbase`, rt.subBase.thickness > 0 ? `${Math.round(rt.subBase.thickness * 1000)}mm thick ${subBaseLabel} Sub base` : `xxmm thick ${subBaseLabel} Sub base`, 'm²').values[road.id] = {
          qty: subBaseOn ? (blockCapping || null) : null,
          formulaInfo: blockCappingFormula,
          naOverride: !subBaseOn,
        };
        const baseCourseOn = isApplicable && rt.baseCourse.thickness > 0;
        addItem(`spec-${specKey}-basecourse`, rt.baseCourse.thickness > 0 ? `${Math.round(rt.baseCourse.thickness * 1000)}mm thick ${baseCourseLabel} DBM base course${baseAttachmentSuffix}` : `xxmm thick ${baseCourseLabel} DBM base course${baseAttachmentSuffix}`, 'm²').values[road.id] = {
          qty: baseCourseOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !baseCourseOn,
        };
        addManualItem(`spec-${specKey}-hotbondbp`, 'Hot bond coat between layers', 'm²', `hot-bond-bp-${specKey}`, road.id, !isApplicable);
        const binderCourseOn = isApplicable && rt.binderCourse.thickness > 0;
        addItem(`spec-${specKey}-bindercourse`, rt.binderCourse.thickness > 0 ? `${Math.round(rt.binderCourse.thickness * 1000)}mm thick ${binderCourseLabel} DBM binder course${binderAttachmentSuffix}` : `xxmm thick ${binderCourseLabel} DBM binder course${binderAttachmentSuffix}`, 'm²').values[road.id] = {
          qty: binderCourseOn ? (specArea || null) : null,
          formulaInfo: specFormula,
          naOverride: !binderCourseOn,
        };
        addItem(`spec-${specKey}-sealinggrit`, 'Sealing grit', 'm²').values[road.id] = { qty: isApplicable ? (specArea || null) : null, formulaInfo: specFormula, naOverride: !isApplicable };
        addItem(`spec-${specKey}-sweep`, 'Sweep', 'm²').values[road.id] = { qty: isApplicable ? (specArea || null) : null, formulaInfo: specFormula, naOverride: !isApplicable };
        const sandBlind = (rt.sandPave ?? 0) > 0 ? ` laid on ${Math.round((rt.sandPave ?? 0) * 1000)}mm sand blinding` : '';
        addItem(`spec-${specKey}-surfacecourse`, `${rt.surfaceCourse.type}${sandBlind}`, 'm²').values[road.id] = { qty: isApplicable ? (specArea || null) : null, formulaInfo: specFormula, naOverride: !isApplicable };
      }
    });

    addSubHeader('subheader-eo-items', 'EO Items');
    addItem('eo-retrofit-ramp', 'EO Retrofit Ramp', 'Nr').values[road.id] = {
      qty: road.eoRamps || null,
      formulaInfo: road.eoRamps ? {
        label: 'EO Retrofit Ramps',
        formula: 'road.eoRamps from carriageway take-off',
        excelRef: 'AG$24',
        rows: [{ ref: road.roadName || `Road ${idx + 1}`, value: road.eoRamps, unit: 'Nr' }],
        total: road.eoRamps,
        unit: 'Nr',
      } : undefined,
    };

    // ── FOOTPATHS, VEHICLE CROSSOVERS & VERGES ──────────────────────────────
    addSection('sec-footpaths', 'FOOTPATHS, VEHICLE CROSSOVERS & VERGES');
    footpathConfigs.forEach(({ areaKey, label, config, excelCol }) => {
      const area = (road[areaKey] as number) || 0;
      addSubHeader(`subheader-footpath-${areaKey}`, label);

      const pathFormula: RoadsSummaryFormulaInfo = {
        label: `${label} area`,
        formula: `road.${areaKey} from carriageway take-off`,
        excelRef: `${excelCol}`,
        rows: [{ ref: label, value: area, unit: 'm²' }],
        total: area,
        unit: 'm²',
      };

      if (areaKey === 'verge') {
        // Verge's own Configure modal (FootpathsConfigModal.tsx) lets this row's topsoil be
        // either "site won topsoil" or "imported topsoil" (constructionType) — the item
        // description must follow that choice instead of always reading "site won".
        const topsoilType = config.constructionType || 'site won topsoil';
        // Verges must always appear in the Carriageway Summary, even when not configured
        // for any road — unlike every other footpath row here, it's exempt from
        // pruneZeroQuantityRoadsSections (alwaysShow) and shows a plain 0 instead of "N/A"
        // when not applicable.
        const vergeRow = addItem(`footpath-${areaKey}-topsoil`, config.topsoil > 0 ? `Spread and level ${Math.round(config.topsoil * 1000)}mm thick ${topsoilType}` : `Spread and level xxmm thick ${topsoilType}`, 'm²');
        vergeRow.alwaysShow = true;
        vergeRow.values[road.id] = {
          qty: config.topsoil > 0 ? (area || 0) : 0, formulaInfo: pathFormula,
        };
        return;
      }

      addItem(`footpath-${areaKey}-membrane`, 'Membrane', 'm²').values[road.id] = {
        qty: config.membrane ? (area || null) : null, formulaInfo: pathFormula, naOverride: !config.membrane,
      };

      if (areaKey === 'tarmacPath' || areaKey === 'tarmacCrossover' || areaKey === 'heavyDutyCrossover' || areaKey === 'blockPavedPathCrossover' || areaKey === 'cycleway') {
        addItem(`footpath-${areaKey}-capping`, config.capping > 0 ? `${Math.round(config.capping * 1000)}mm thick 6F2 capping layer` : 'xxmm thick 6F2 capping layer', 'm²').values[road.id] = {
          qty: config.capping > 0 ? (area || null) : null, formulaInfo: pathFormula, naOverride: config.capping <= 0,
        };
        addItem(`footpath-${areaKey}-subbase`, config.subBase > 0 ? `${Math.round(config.subBase * 1000)}mm thick MoT type 1 Sub base` : 'xxmm thick MoT type 1 Sub base', 'm²').values[road.id] = {
          qty: config.subBase > 0 ? (area || null) : null, formulaInfo: pathFormula, naOverride: config.subBase <= 0,
        };
        const baseCourseType = footpathsConfig.baseCourseType ?? 'AC32';
        const binderCourseType = footpathsConfig.binderCourseType ?? 'AC20';
        const baseLabel = `${config.base > 0 ? `${Math.round(config.base * 1000)}mm` : 'xxmm'} thick ${baseCourseType} DBM base course${formatBaseAttachments(footpathsConfig.baseAttachments)}`;
        addItem(`footpath-${areaKey}-base`, baseLabel, 'm²').values[road.id] = {
          qty: config.base > 0 ? (area || null) : null, formulaInfo: pathFormula, naOverride: config.base <= 0,
        };
        const binderLabel = `${config.binder > 0 ? `${Math.round(config.binder * 1000)}mm` : 'xxmm'} thick ${binderCourseType} DBM binder course${formatBaseAttachments(footpathsConfig.binderAttachments)}`;
        addItem(`footpath-${areaKey}-binder`, binderLabel, 'm²').values[road.id] = {
          qty: config.binder > 0 ? (area || null) : null, formulaInfo: pathFormula, naOverride: config.binder <= 0,
        };
        addItem(`footpath-${areaKey}-sealinggrit`, 'Sealing Grit', 'm²').values[road.id] = {
          qty: area || null, formulaInfo: pathFormula,
        };
        addItem(`footpath-${areaKey}-tackcoat`, 'Sweep & tack coat', 'm²').values[road.id] = {
          qty: area || null, formulaInfo: pathFormula,
        };
        const ac6SurfaceLabelMap: Record<number, string> = { 0.02: '20mm thick AC6 DB surface course', 0.025: '25mm thick AC6 DB surface course', 0.03: '30mm thick AC6 DB surface course' };
        const surfaceDesc = config.constructionType === 'AC6 DBM' && ac6SurfaceLabelMap[config.surface]
          ? ac6SurfaceLabelMap[config.surface]
          : config.surface > 0
            ? `${Math.round(config.surface * 1000)}mm thick ${config.constructionType} surface course`
            : `xxmm thick ${config.constructionType} surface course`;
        addItem(`footpath-${areaKey}-surface`, surfaceDesc, 'm²').values[road.id] = {
          qty: config.surface > 0 ? (area || null) : null, formulaInfo: pathFormula, naOverride: config.surface <= 0,
        };
        if (areaKey === 'cycleway') {
          addManualItem(`footpath-${areaKey}-topsoil`, 'Regulating to footpaths', 'm²', 'regulating-cycleway', road.id);
        }
        return;
      }
    });

    // ── KERBS & EDGINGS ──────────────────────────────────────────────────────
    addSection('sec-kerbs', 'KERBS & EDGINGS');
    const kerbDefs: [string, string, (r: CarriagewayFootway) => number, string][] = [
      ['Kerb Race', 'm', (r) => r.kerbRace || 0, 'L$24'],
      ['Extruded Kerb Race', 'm', (r) => r.extrudedKerbRace || 0, 'Q$24'],
      ['Temporary Kerb / Block to race', 'm', (r) => r.tempKerbBlock || 0, 'R$24'],
      ['Remove Temporary Kerb / Block', 'm', (r) => r.removeTempKerbBlock || 0, 'S$24'],
      ['PCC Kerbs - All Sizes (including race)', 'm', (r) => r.kerb10x5IncRace || 0, 'T$24'],
      ['10×5 Kerb on race', 'm', (r) => r.kerb10x5OnRace || 0, 'U$24'],
      ['6×5 Kerb on race', 'm', (r) => r.kerb6x5OnRace || 0, 'V$24'],
      ['Drop Kerb', 'm', (r) => r.dropKerb || 0, 'W$24'],
      ['Delineation Kerb', 'm', (r) => r.delineationKerb || 0, 'X$24'],
      ['EO Radius Kerb', 'm', (r) => r.eoRadiusKerb || 0, 'Y$24'],
      ['EO Radius Drop Kerb', 'm', (r) => r.eoRadiusDropKerb || 0, 'Z$24'],
      ['Kassel Bus Stop Kerbs', 'm', (r) => r.kasselKerb || 0, 'AA$24'],
      ['Trief Kerb', 'm', (r) => r.triefKerb || 0, 'AB$24'],
      ['Beany Drainage Kerb', 'm', (r) => r.beanyKerb || 0, 'AC$24'],
      ['Beany Drainage Gully', 'Nr', (r) => r.beanyGully || 0, 'AD$24'],
      ['Aco Kerbdrain 305', 'm', (r) => r.acoKerbDrain || 0, 'AE$24'],
      ['Aco Kerbdrain 305 Gully', 'Nr', (r) => r.acoKerbGully || 0, 'AF$24'],
      ['EO Ramps', 'Nr', (r) => r.eoRamps || 0, 'AG$24'],
      ['EO for 50×50mm key formed in kerb race', 'm', (r) => r.keyChannel || 0, 'M$24'],
      ['EO for xxmmØ dowel to kerb race at xxxmm ccs', 'Nr', (r) => r.dowelBarsInRace || 0, 'M$24'],
      ['Temporary macadam fillet to drive crossover prior to road surfacing', 'm', (r) => r.filletToCrossover || 0, 'O$24'],
      ['Remove macadam fillet upon completion', 'm', (r) => r.removeFillet || 0, 'P$24'],
      ['150×50 pcc edging', 'm', (r) => r.edgings150x50 || 0, 'AP$24'],
      ['200×50 pcc edging', 'm', (r) => r.edgings200x50 || 0, 'AQ$24'],
      ['Tactile Paving', 'Nr', (r) => r.tactilePaving || 0, 'AR$24'],
      ['Corduroy Paving', 'Nr', (r) => r.corduroyPaving || 0, 'AS$24'],
    ];
    kerbDefs.forEach(([desc, unit, fn, excelRef], kIdx) => {
      const qty = fn(road);
      addItem(`kerb-${kIdx}`, desc, unit).values[road.id] = {
        qty: qty || null,
        formulaInfo: qty > 0 ? {
          label: desc,
          formula: 'From carriageway take-off data',
          excelRef,
          rows: [{ ref: road.roadName || `Road ${idx + 1}`, value: qty, unit }],
          total: qty,
          unit,
        } : undefined,
      };
    });

    // ── Placeholder sections (manual/yellow input fields) ─────────────────────
    addSection('sec-ironwork', 'ADJUST IRONWORK');
    addManualItem('ironwork-manholes-single', 'Manholes - single cover and frame', 'Nr', 'ironwork-manholes-single', road.id);
    addManualItem('ironwork-manholes-double', 'Manholes - double cover and frame', 'Nr', 'ironwork-manholes-double', road.id);
    addManualItem('ironwork-gully-covers', 'Gully covers and frames', 'Nr', 'ironwork-gully-covers', road.id);
    addManualItem('ironwork-stop-tap', 'Stop Tap Boxes', 'Nr', 'ironwork-stop-tap', road.id);
    addManualItem('ironwork-bt-virgin', 'BT / Virgin footway boxes', 'Nr', 'ironwork-bt-virgin', road.id);

    addSection('sec-white-lining', 'WHITE LINING');
    addManualItem('white-lining', 'White lining', 'Item', 'white-lining', road.id);

    addSection('sec-street-nameplates', 'FIX-ONLY STREET NAMEPLATES');
    addManualItem('street-nameplates', 'Fix-only street nameplates', 'Nr', 'street-nameplates', road.id);

    addSection('sec-tree-bollards', 'TREE PITS, BOLLARDS & STREET FURNITURE');
    addManualItem('tree-pits', 'Tree pits', 'Nr', 'tree-pits', road.id);
    addManualItem('bollards', 'Bollards', 'Nr', 'bollards', road.id);

    addSection('sec-remedials', 'REMEDIALS');
    addManualItem('remedials-kerb', 'Kerb remedial', 'Nr', 'remedials-kerb', road.id);
    addManualItem('remedials-edging', 'Edging remedial', 'Nr', 'remedials-edging', road.id);

    addSection('sec-post-rail', 'POST & RAIL FENCING');
    addManualItem('post-rail-fencing', 'Post & Rail Fencing', 'm', 'post-rail-fencing', road.id);

    addSection('sec-armco', 'ARMCO BARRIER');
    addManualItem('armco-barrier', 'Armco barrier', 'm', 'armco-barrier', road.id);

    addSection('sec-signage', 'SIGNAGE');
    addManualItem('signage-supply-install', 'Supply & install signs', 'Item', 'signage-supply-install', road.id);
    addManualItem('signage-keep-left-illuminated', 'Keep left Illuminated bollard', 'Nr', 'signage-keep-left-illuminated', road.id);
    addManualItem('signage-keep-left-reflective', 'Keep left reflective bollard', 'Nr', 'signage-keep-left-reflective', road.id);
  });

  return (options?.pruneZeroQty ?? true) ? pruneZeroQuantityRoadsSections(sectionList) : sectionList;
};

/** Sums a Carriageway Summary section tree's Amount (Σ qty across roads × item-library
 * rate resolved from each row's evaluateCode via the caller's own lookup) — rows with no
 * code or no library match contribute 0, same convention every other bill summary uses. */
export const sumRoadsBillSummaryAmount = (
  sections: RoadsSummarySectionNode[],
  getRate: (code: string) => number | undefined,
): number => {
  let total = 0;
  sections.forEach((section) => {
    section.children.forEach((row) => {
      if (row.rowType !== 'item' || !row.evaluateCode) return;
      const rate = getRate(row.evaluateCode);
      if (rate == null) return;
      const qty = Object.values(row.values).reduce((sum, v) => sum + (v.qty ?? 0), 0);
      total += qty * rate;
    });
  });
  return total;
};
